import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { MapContainer, TileLayer, useMapEvents, Marker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import {
  reverseGeocode,
  searchPlaces,
  getCurrentPosition,
  reverseGeocodeAddress,
  validatePincode,
  getAddressByPincode,
  getGPSErrorMessage,
} from "../../Features/Utils/locationHelper";
import { setLocation } from "../../Features/User/userSlice";
import { toastSuccess } from "../../api/services";

const pinIcon = new L.DivIcon({
  html: '<div style="position: relative;"><img src="https://cdn-icons-png.flaticon.com/512/684/684908.png" style="width: 35px; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));" /></div>',
  iconSize: [35, 35],
  iconAnchor: [17, 35],
  popupAnchor: [0, -35],
  className: 'custom-pin-marker'
});

const DEFAULT_POSITION = [28.6139, 77.209];
const DEBOUNCE_DELAY = 500;

// Search Autocomplete Component
function SearchAutocomplete({
  mapRef,
  setPosition,
  setAddress,
  setPincode,
  setLoading,
  searchTerm,
  setSearchTerm,
  updateFormFields,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const timeoutRef = useRef(null);

  const handleSearch = useCallback(
    (value) => {
      setSearchTerm(value);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(async () => {
        if (!value) return setSuggestions([]);
        try {
          const data = await searchPlaces(value);
          setSuggestions(data || []);
        } catch {
          setSuggestions([]);
        }
      }, DEBOUNCE_DELAY);
    },
    [setSearchTerm],
  );

  const handleSelect = async (item) => {
    const lat = Number.parseFloat(item.lat);
    const lng = Number.parseFloat(item.lon);
    setPosition([lat, lng]);
    setSearchTerm(item.display_name);
    setLoading(true);
    if (mapRef.current) {
      mapRef.current.setView([lat, lng], 16);
    }
    try {
      const result = await reverseGeocode(lat, lng);
      if (result) {
        setAddress(result.shortAddress);
        setPincode(result.pincode);
        updateFormFields({
          street: result.fullAddress || result.shortAddress,
          city: result.city || "",
          state: result.state || "",
          zipCode: result.pincode || "",
        });
      }
    } finally {
      setSuggestions([]);
      setLoading(false);
    }
  };

  return (
    <div className="search-autocomplete">
      <input
        value={searchTerm}
        onChange={(e) => handleSearch(e.target.value)}
        placeholder="Search location..."
        className="search-input"
      />
      {suggestions.length > 0 && (
        <div className="suggestions-list">
          {suggestions.map((s, index) => (
            <div
              key={
                s.place_id ? String(s.place_id) : `${s.display_name}-${index}`
              }
              onClick={() => handleSelect(s)}
              className="suggestion-item"
            >
              📍 {s.display_name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

SearchAutocomplete.propTypes = {
  mapRef: PropTypes.object.isRequired,
  setPosition: PropTypes.func.isRequired,
  setAddress: PropTypes.func.isRequired,
  setPincode: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  searchTerm: PropTypes.string.isRequired,
  setSearchTerm: PropTypes.func.isRequired,
  updateFormFields: PropTypes.func.isRequired,
};

// Map Controller Component
function MapController({
  setPosition,
  setAddress,
  setPincode,
  setSearchTerm,
  setDragging,
  updateFormFields,
}) {
  const timerRef = useRef(null);
  const map = useMapEvents({
    dragstart: () => {
      setDragging(true);
    },
    drag: () => {
      setDragging(true);
    },
    dragend: () => {
      setDragging(false);
      const center = map.getCenter();
      const lat = center.lat;
      const lng = center.lng;
      setPosition([lat, lng]);

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(async () => {
        try {
          const result = await reverseGeocode(lat, lng);
          if (result) {
            setAddress(result.shortAddress);
            setPincode(result.pincode);
            setSearchTerm(result.shortAddress);
            updateFormFields({
              street: result.fullAddress || result.shortAddress,
              city: result.city || "",
              state: result.state || "",
              zipCode: result.pincode || "",
            });
          }
        } catch (error) {
          console.error("Reverse geocoding error:", error);
        }
      }, 300);
    },
  });

  return null;
}

MapController.propTypes = {
  setPosition: PropTypes.func.isRequired,
  setAddress: PropTypes.func.isRequired,
  setPincode: PropTypes.func.isRequired,
  setSearchTerm: PropTypes.func.isRequired,
  setDragging: PropTypes.func.isRequired,
  updateFormFields: PropTypes.func.isRequired,
};

// Center Marker Component
function CenterMarker({ dragging }) {
  const map = useMapEvents({});
  const [position, setPosition] = useState(map.getCenter());
  
  useEffect(() => {
    const updatePosition = () => {
      setPosition(map.getCenter());
    };
    
    map.on('move', updatePosition);
    map.on('drag', updatePosition);
    
    return () => {
      map.off('move', updatePosition);
      map.off('drag', updatePosition);
    };
  }, [map]);
  
  return <Marker position={position} icon={pinIcon} interactive={false} />;
}

CenterMarker.propTypes = {
  dragging: PropTypes.bool,
};

// Main Modal Component
export default function UnifiedAddressModal({
  isOpen,
  onClose,
  existingAddress,
  editAddressId,
  userId,
  addressCreate,
  addressUpdate,
  fetchAddresses,
  addresses = [],
}) {
  const dispatch = useDispatch();
  
  // ✅ Function to get default address type
  const getDefaultAddressType = useCallback(() => {
    // If editing existing address, use its label
    if (existingAddress && existingAddress.label) {
      return existingAddress.label;
    }
    
    // Check if addresses array exists
    if (!addresses || addresses.length === 0) {
      return "Home"; // No addresses exist, default to Home
    }
    
    // Check which address types already exist
    const existingLabels = addresses.map(addr => addr.label);
    const hasHome = existingLabels.includes("Home");
    const hasWork = existingLabels.includes("Work");
    
    // If Home doesn't exist, default to Home
    if (!hasHome) {
      return "Home";
    }
    
    // If Home exists but Work doesn't, default to Work
    if (hasHome && !hasWork) {
      return "Work";
    }
    
    // If both Home and Work exist, default to Other
    if (hasHome && hasWork) {
      return "Other";
    }
    
    // Fallback
    return "Other";
  }, [addresses, existingAddress]);

  const [formData, setFormData] = useState({
    label: getDefaultAddressType(), // ✅ Set default based on existing addresses
    street: "",
    city: "",
    state: "",
    country: "India",
    zipCode: "",
    order_note: "",
  });

  const [position, setPosition] = useState(DEFAULT_POSITION);
  const [address, setAddress] = useState("");
  const [pincode, setPincode] = useState("");
  const [loading, setLoading] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState("");
  const [pincodeData, setPincodeData] = useState(null);
  const [mapKey, setMapKey] = useState(Date.now());
  const [mapReady, setMapReady] = useState(false);

  const mapRef = useRef(null);

  const updateFormFields = useCallback((fields) => {
    setFormData((prev) => ({
      ...prev,
      street: fields.street !== undefined ? fields.street : prev.street,
      city: fields.city !== undefined ? fields.city : prev.city,
      state: fields.state !== undefined ? fields.state : prev.state,
      zipCode: fields.zipCode !== undefined ? fields.zipCode : prev.zipCode,
    }));
  }, []);

  // GPS Location Detection
  const detectCurrentLocation = useCallback(async () => {
    setGpsLoading(true);
    setError("");
    try {
      const currentPos = await getCurrentPosition();
      const { latitude, longitude } = currentPos.coords;
      const newPosition = [latitude, longitude];
      setPosition(newPosition);

      if (mapRef.current) {
        mapRef.current.setView(newPosition, 16);
      }

      const addressResult = await reverseGeocodeAddress(latitude, longitude);
      if (addressResult) {
        const fullAddress =
          addressResult.fullAddress || addressResult.shortAddress;
        setAddress(fullAddress);
        setPincode(addressResult.pincode);
        setSearchTerm(fullAddress);
        updateFormFields({
          street: fullAddress,
          city: addressResult.city || "",
          state: addressResult.state || "",
          zipCode: addressResult.pincode || "",
        });
      }
      toastSuccess("Location detected successfully");
    } catch (error) {
      console.error("Could not detect current location:", error);
      setError(getGPSErrorMessage(error));
    } finally {
      setGpsLoading(false);
    }
  }, [updateFormFields]);

  // ✅ Reset form when modal opens or addresses change
  useEffect(() => {
    if (isOpen) {
      if (existingAddress && editAddressId) {
        // Editing existing address
        setFormData({
          label: existingAddress.label || getDefaultAddressType(),
          street: existingAddress.street || "",
          city: existingAddress.city || "",
          state: existingAddress.state || "",
          country: existingAddress.country || "India",
          zipCode: existingAddress.zipCode || "",
          order_note: existingAddress.order_note || "",
        });
        if (existingAddress.lat && existingAddress.lng) {
          const newPosition = [existingAddress.lat, existingAddress.lng];
          setPosition(newPosition);
          setAddress(existingAddress.street || "");
          setPincode(existingAddress.zipCode || "");
          setSearchTerm(existingAddress.street || "");
        } else if (existingAddress.street) {
          setAddress(existingAddress.street);
          setPincode(existingAddress.zipCode);
          setSearchTerm(existingAddress.street);
        }
      } else {
        // Adding new address - set default based on existing addresses
        setFormData(prev => ({
          ...prev,
          label: getDefaultAddressType(),
          street: "",
          city: "",
          state: "",
          country: "India",
          zipCode: "",
          order_note: "",
        }));
      }
      setMapKey(Date.now());
      setMapReady(false);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [isOpen, existingAddress, editAddressId, addresses, getDefaultAddressType]);

  // ✅ Check if address label is available (not already used)
  const isLabelAvailable = (label, currentAddressId = null) => {
    // Only check for Home and Work labels (Other can have multiple)
    if (label !== "Home" && label !== "Work") return true;
    
    return !addresses.some(
      (addr) => addr.label === label && addr.address_id !== currentAddressId,
    );
  };

  // ✅ Check if radio button should be disabled
  const isRadioDisabled = (label) => {
    if (editAddressId) {
      const current = addresses.find((a) => a.address_id === editAddressId);
      // If editing current address, allow its own label
      if (current && current.label === label) return false;
      // For other labels, check availability
      return !isLabelAvailable(label, editAddressId);
    } else {
      // For new address, check availability
      return !isLabelAvailable(label, null);
    }
  };

  // ✅ Get tooltip text for disabled radio
  const getDisabledTooltip = (label) => {
    if (label === "Home") return "You already have a Home address";
    if (label === "Work") return "You already have a Work address";
    return "This option is not available";
  };

  const getAddressFromGPS = async () => {
    await detectCurrentLocation();
  };

  const handlePincodeChange = async (zipCode) => {
    setFormData({ ...formData, zipCode });
    if (validatePincode(zipCode)) {
      try {
        const data = await getAddressByPincode(zipCode);
        setPincodeData(data);
        setFormData((prev) => ({
          ...prev,
          city: data.city || prev.city,
          state: data.state || prev.state,
        }));
        setError("");
      } catch (error) {
        console.error("Pincode error:", error);
        setPincodeData(null);
      }
    } else {
      setPincodeData(null);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.label) {
      setError("Please select an address type");
      return;
    }
    if (!isLabelAvailable(formData.label, editAddressId)) {
      setError(
        `You already have a "${formData.label}" address. Only one allowed.`,
      );
      return;
    }
    if (!formData.street && !address) {
      setError("Please select or enter an address");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const addressData = {
        label: formData.label,
        street: formData.street || address,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        zipCode: formData.zipCode || pincode,
        order_note: formData.order_note,
        lat: position[0],
        lng: position[1],
      };

      console.log("Saving address data:", addressData);

      if (editAddressId) {
        await addressUpdate({ id: editAddressId, ...addressData });
        toastSuccess("Address updated successfully");
      } else {
        await addressCreate({ userId, ...addressData });
        toastSuccess("Address added successfully");
      }

      dispatch(setLocation({ location: addressData }));

      if (fetchAddresses) await fetchAddresses();
      onClose();
    } catch (err) {
      console.error("Save error:", err);
      setError(
        err.response?.data?.message || err.message || "Failed to save address",
      );
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="unified-address-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content-address">
          <div className="modal-header-address">
            <h2>{editAddressId ? "Edit Address" : "Add New Address"}</h2>
            <button className="close-address" onClick={onClose} type="button">
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit} className="address-form">
            <div className="modal-body-address">
              {error && (
                <div className="error-message" role="alert">
                  <span>⚠️</span> {error}
                  <button type="button" onClick={() => setError("")}>
                    ✕
                  </button>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Address Type *</label>
                <div className="radio-group">
                  {["Home", "Work", "Other"].map((type) => {
                    const isDisabled = isRadioDisabled(type);
                    return (
                      <label 
                        key={type} 
                        className={`radio-label ${isDisabled ? 'disabled' : ''}`}
                        title={isDisabled ? getDisabledTooltip(type) : ""}
                      >
                        <input
                          type="radio"
                          name="label"
                          value={type}
                          checked={formData.label === type}
                          onChange={handleChange}
                          disabled={isDisabled}
                        />
                        <span>{type}</span>
                        {isDisabled && (
                          <span className="disabled-badge">(Already exists)</span>
                        )}
                      </label>
                    );
                  })}
                </div>
                <div className="form-hint">
                  {addresses.filter(a => a.label === "Home" || a.label === "Work").length === 2 && (
                    <small>⚠️ You can only have one Home and one Work address. Other addresses can be multiple.</small>
                  )}
                </div>
              </div>

              <SearchAutocomplete
                mapRef={mapRef}
                setPosition={setPosition}
                setAddress={setAddress}
                setPincode={setPincode}
                setLoading={setLoading}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                updateFormFields={updateFormFields}
              />

              <div className="map-section">
                <div className="map-header">
                  <span>🗺️ Drag map to position marker</span>
                  <button
                    type="button"
                    className="gps-icon-btn"
                    onClick={getAddressFromGPS}
                    disabled={gpsLoading}
                  >
                    📡 {gpsLoading ? "Detecting..." : "Use GPS"}
                  </button>
                </div>
                <div className="map-wrapper">
                  <MapContainer
                    key={mapKey}
                    center={position}
                    zoom={14}
                    style={{
                      height: "100%",
                      width: "100%",
                      borderRadius: "12px",
                    }}
                    whenReady={() => setMapReady(true)}
                    ref={mapRef}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {mapReady && (
                      <>
                        <CenterMarker dragging={dragging} />
                        <MapController
                          setPosition={setPosition}
                          setAddress={setAddress}
                          setPincode={setPincode}
                          setSearchTerm={setSearchTerm}
                          setDragging={setDragging}
                          updateFormFields={updateFormFields}
                        />
                      </>
                    )}
                  </MapContainer>
                </div>
                <div className="selected-address">
                  <strong>Selected:</strong>{" "}
                  {address ||
                    (dragging
                      ? "Drag to select location..."
                      : loading
                        ? "Detecting location..."
                        : "Drag map to select location")}
                  {pincode && <span className="pincode">, {pincode}</span>}
                </div>
              </div>

              <div className="manual-section">
                <div className="section-divider">
                  <span>OR Enter Manually</span>
                </div>

                <div className="form-group">
                  <label htmlFor="street">Street Address *</label>
                  <textarea
                    id="street"
                    name="street"
                    className="form-control"
                    rows="3"
                    placeholder="House No., Street, Area, Landmark"
                    value={formData.street}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="zipCode">Pincode *</label>
                    <input
                      id="zipCode"
                      name="zipCode"
                      type="text"
                      className="form-control"
                      placeholder="6 digit pincode"
                      value={formData.zipCode}
                      onChange={(e) => handlePincodeChange(e.target.value)}
                      maxLength="6"
                    />
                    {pincodeData && (
                      <div className="success-message">
                        ✓ {pincodeData.area}, {pincodeData.city}
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="city">City *</label>
                    <input
                      id="city"
                      name="city"
                      type="text"
                      className="form-control"
                      placeholder="City/District"
                      value={formData.city}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="state">State *</label>
                    <input
                      id="state"
                      name="state"
                      type="text"
                      className="form-control"
                      placeholder="State"
                      value={formData.state}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="country">Country *</label>
                    <input
                      id="country"
                      name="country"
                      type="text"
                      className="form-control"
                      placeholder="Country"
                      value={formData.country}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="order_note">
                    Order Note / Remark (optional)
                  </label>
                  <textarea
                    id="order_note"
                    name="order_note"
                    className="form-control"
                    rows="2"
                    placeholder="Any special instructions for delivery"
                    value={formData.order_note}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button type="submit" className="btn-submit" disabled={loading}>
                {loading ? <span className="spinner"></span> : null}
                {editAddressId ? "Update Address" : "Save Address"}
              </button>
              <button type="button" className="btn-cancel" onClick={onClose}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
        }

        .unified-address-modal {
          border: none;
          border-radius: 16px;
          padding: 0;
          width: 90%;
          max-width: 700px;
          background: #fff;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          animation: modalFadeIn 0.3s ease;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
        }

        @keyframes modalFadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .modal-content-address {
          display: flex;
          flex-direction: column;
          height: 100%;
          overflow: hidden;
        }

        .modal-header-address {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #e5e7eb;
          background: #fff;
          flex-shrink: 0;
        }

        .modal-header-address h2 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 600;
          color: #1f2937;
        }

        .close-address {
          background: none;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          height: 32px;
          width: 32px;
          font-size: 18px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .close-address:hover {
          background: #f3f4f6;
          transform: scale(1.05);
        }

        .address-form {
          display: flex;
          flex-direction: column;
          overflow: hidden;
          flex: 1;
        }

        .modal-body-address {
          padding: 24px;
          overflow-y: auto;
          flex: 1;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-label {
          display: block;
          margin-bottom: 8px;
          font-weight: 500;
          color: #374151;
        }

        .radio-group {
          display: flex;
          gap: 24px;
          flex-wrap: wrap;
        }

        .radio-label {
          display: flex;
          align-items: center;
          cursor: pointer;
          gap: 6px;
        }

        .radio-label.disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }

        .radio-label input {
          cursor: pointer;
        }

        .radio-label.disabled input {
          cursor: not-allowed;
        }

        .radio-label input:disabled + span {
          opacity: 0.6;
        }

        .disabled-badge {
          font-size: 10px;
          color: #ef4444;
          background: #fee2e2;
          padding: 2px 6px;
          border-radius: 10px;
          margin-left: 4px;
        }

        .form-hint {
          margin-top: 6px;
          font-size: 11px;
          color: #6b7280;
        }

        .search-autocomplete {
          position: relative;
          margin-bottom: 20px;
        }

        .search-input {
          width: 100%;
          padding: 12px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          transition: all 0.2s;
        }

        .search-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .suggestions-list {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          max-height: 200px;
          overflow-y: auto;
          z-index: 1000;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .suggestion-item {
          padding: 10px 12px;
          cursor: pointer;
          border-bottom: 1px solid #f3f4f6;
          font-size: 13px;
          transition: all 0.2s;
        }

        .suggestion-item:hover {
          background: #f9fafb;
          padding-left: 16px;
        }

        .map-section {
          margin-bottom: 24px;
        }

        .map-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .gps-icon-btn {
          background: #3b82f6;
          color: #fff;
          border: none;
          padding: 6px 12px;
          border-radius: 20px;
          cursor: pointer;
          font-size: 12px;
          transition: all 0.2s;
        }

        .gps-icon-btn:hover:not(:disabled) {
          background: #2563eb;
          transform: translateY(-1px);
        }

        .gps-icon-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .map-wrapper {
          position: relative;
          margin-bottom: 12px;
          height: 250px;
          background: #f3f4f6;
          border-radius: 12px;
          overflow: hidden;
          z-index: 5;
        }

        .selected-address {
          padding: 10px;
          background: #f3f4f6;
          border-radius: 8px;
          font-size: 13px;
          color: #374151;
        }

        .pincode {
          color: #6b7280;
        }

        .manual-section {
          margin-top: 8px;
        }

        .section-divider {
          text-align: center;
          margin: 20px 0;
          position: relative;
        }

        .section-divider::before {
          content: "";
          position: absolute;
          top: 50%;
          left: 0;
          right: 0;
          height: 1px;
          background: #e5e7eb;
        }

        .section-divider span {
          background: #fff;
          padding: 0 12px;
          position: relative;
          color: #6b7280;
          font-size: 12px;
        }

        .form-control {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          transition: all 0.2s;
        }

        .form-control:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-bottom: 16px;
        }

        .success-message {
          margin-top: 6px;
          font-size: 12px;
          color: #10b981;
        }

        .error-message {
          background: #fee2e2;
          color: #dc2626;
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .error-message button {
          background: none;
          border: none;
          color: #dc2626;
          cursor: pointer;
          font-size: 18px;
          padding: 0 4px;
        }

        .modal-footer {
          display: flex;
          gap: 12px;
          padding: 16px 24px;
          border-top: 1px solid #e5e7eb;
          background: #fff;
          flex-shrink: 0;
        }

        .btn-submit,
        .btn-cancel {
          flex: 1;
          padding: 12px;
          border: none;
          border-radius: 8px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-submit {
          background: #3b82f6;
          color: #fff;
        }

        .btn-submit:hover:not(:disabled) {
          background: #2563eb;
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
        }

        .btn-submit:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .btn-cancel {
          background: #f3f4f6;
          color: #374151;
        }

        .btn-cancel:hover {
          background: #e5e7eb;
          transform: translateY(-1px);
        }

        .spinner {
          width: 16px;
          height: 16px;
          border: 2px solid #fff;
          border-top-color: transparent;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
          display: inline-block;
          margin-right: 8px;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        /* Scrollbar styling */
        .modal-body-address::-webkit-scrollbar {
          width: 6px;
        }

        .modal-body-address::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }

        .modal-body-address::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }

        .modal-body-address::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
      `}</style>
    </div>
  );
}

UnifiedAddressModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  existingAddress: PropTypes.object,
  editAddressId: PropTypes.string,
  userId: PropTypes.number,
  addressCreate: PropTypes.func,
  addressUpdate: PropTypes.func,
  fetchAddresses: PropTypes.func,
  addresses: PropTypes.array,
};